import React, { useEffect, useState } from "react";
import styles from "./forgot-password.module.css";
import { Controller, useForm } from "react-hook-form";
import CustomTextfield from "../../../common/input/custom-textfield/custom-textfield";
import { Spin } from "antd";
import CustomButton from "../../../common/custom-button/custom-button";
import { forgotPassword, resetPassword } from "../../../services/auth";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import RoutesConstant from "../../../constants/routes";
import CustomOtpInput from "../../../common/input/custom-otp-input/custom-otp-input";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userValidated, setUserValidated] = useState(false);
  const [visible, setVisible] = useState(false);
  const [timer, setTimer] = useState(30);
  const { handleSubmit, control, getValues } = useForm({
    mode: "onChange",
    defaultValues: {
      username: "",
      otp: "",
      new_password: "",
      confirm_password: "",
    },
  });

  useEffect(() => {
    if (timer > 0) {
      const countdown = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(countdown);
    }
  }, [timer]);

  const handleResend = async () => {
    const formValues = getValues();
    await handleForgotPassword(formValues);
  };
  
  const handleForgotPassword = async (values) => {
    try {
      setLoading(true);
      const payload = {
        user_name: values?.username,
      };
      await forgotPassword(payload);
      setUserValidated(true);
      setTimer(30);
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.message || "Error! please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (values) => {
    try {
      setLoading(true);
      if (values?.new_password !== values?.confirm_password) {
        toast.error("New password and confirm password do not match.");
        return;
      }

      const payload = {
        user_name: values?.username,
        otp: values?.otp,
        new_password: values?.new_password,
      };
      await resetPassword(payload);
      toast.success("Password reset successfully");
      setTimeout(() => {
        navigate(RoutesConstant.LOGIN);
      }, 500);
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.message || "Error! please try again");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values) => {
    if (userValidated) {
      await handleResetPassword(values);
    } else {
      await handleForgotPassword(values);
    }
  };

  return (
    <Spin spinning={loading} className={styles.pageLoader}>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.headerContent}>
            <img
              src="/assets/images/logo.svg"
              alt="Logo"
              className={styles.logo}
            />
            <h2 className={styles.title}>Forgot Password</h2>
            <p className={styles.subtitle}>
              {"Let’s get you back into your account. Enter your details below."}
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="username"
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id="username"
                  label="User Name"
                  showRequired
                  labelColor="#434343"
                  placeholder="Enter user name"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                  disabled={userValidated}
                />
              )}
            />

            {userValidated ? (
              <>
                <Controller
                  name="new_password"
                  control={control}
                  rules={{ required: true }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <CustomTextfield
                      id="new_password"
                      label="New Password"
                      showRequired
                      labelColor="#434343"
                      placeholder="Enter a new password"
                      type={visible ? "text" : "password"}
                      suffix={
                        visible ? (
                          <EyeTwoTone onClick={() => setVisible(!visible)} />
                        ) : (
                          <EyeInvisibleOutlined
                            onClick={() => setVisible(!visible)}
                          />
                        )
                      }
                      size="large"
                      value={value}
                      onChange={onChange}
                      status={error ? "error" : ""}
                    />
                  )}
                />
                <Controller
                  name="confirm_password"
                  control={control}
                  rules={{ required: true }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <CustomTextfield
                      id="confirm_password"
                      label="Confirm Password"
                      showRequired
                      labelColor="#434343"
                      placeholder="Re-enter password"
                      type="password"
                      size="large"
                      value={value}
                      onChange={onChange}
                      status={error ? "error" : ""}
                    />
                  )}
                />
                <div>
                  <div className={styles.otpWrapper}>
                    <Controller
                      name="otp"
                      control={control}
                      rules={{ required: true }}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <CustomOtpInput
                          id="otp"
                          label="OTP"
                          showRequired
                          otpLength={6}
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          status={error ? "error" : ""}
                        />
                      )}
                    />
                    <CustomButton
                      title="Resend OTP"
                      className={styles.resendButton}
                      disabled={timer > 0}
                      onClick={handleResend}
                    />
                  </div>
                  <p className={styles.timerText}>
                    {"Didn’t receive the OTP? You can resend it"}
                    {timer > 0 ? ` in ${timer} second${timer > 1 ? "s" : ""}.` : "."}
                  </p>
                </div>
              </>
            ) : null}

            <CustomButton
              title={userValidated ? "Submit" : "Send OTP"}
              type="primary"
              htmlType="submit"
              className={styles.button}
            />
          </form>
        </div>
        <div className={styles.loginMessage}>
          Already know your password?{" "}
          <Link to={RoutesConstant.LOGIN}>Log in</Link>{" "}
          here
        </div>
      </div>
    </Spin>
  );
};

export default ForgotPassword;
